import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Image, Tooltip } from 'antd';
import { sum } from 'lodash';
import CgmBarChartItem from '../../../../assets/icons/cgmBarChartItem.svg';
import CgmBarChartLabelDm2 from '../../../../assets/icons/cgmBarChartLabelDm2.svg';
import CgmBarChartLabelPreDm from '../../../../assets/icons/cgmBarChartLabelPreDm.svg';
import { CgmMetricEnum, CgmPercentGoal } from '../../../../uc-api-sdk';
import { TagComponent, TagType } from '../../../../uiComponent/TagComponent/TagComponent';
import { TextComponent } from '../../../../uiComponent/TextComponent/TextComponent';
import { YAxis } from './CgmBarChartYAxisComponent';
import { CgmEmptySummaryComponent } from './CgmEmptySummaryComponent';
import './CgmVitalSummaryComponent.scss';

export interface CgmBarChartComponentProps {
  tbrLevel2Min?: number;
  tbrLevel1Min?: number;
  tirMin?: number;
  tarLevel1Min?: number;
  tarLevel2Min?: number;
  percentGoals?: CgmPercentGoal[];
  showGoals?: boolean;
}

export const CgmBarChartComponent = ({
  tbrLevel2Min,
  tbrLevel1Min,
  tirMin,
  tarLevel1Min,
  tarLevel2Min,
  percentGoals,
  showGoals = false
}: CgmBarChartComponentProps) => {
  const isDataValid = [tbrLevel2Min, tbrLevel1Min, tirMin, tarLevel1Min, tarLevel2Min].some(
    value => value !== undefined
  );

  const barChartHeight = 110;

  const tagTypes: Map<string, TagType> = new Map([
    ['Very High', 'error'],
    ['High', 'warning'],
    ['TIR', 'success'],
    ['Low', 'processing'],
    ['Very Low', 'default']
  ]);

  const getMetricGoal = (
    metricName: CgmMetricEnum
  ) => percentGoals?.find(i => i.metric === metricName)?.goal || 0;

  const totalMin = sum([tbrLevel2Min, tbrLevel1Min, tirMin, tarLevel1Min, tarLevel2Min]);

  const tbrLevel2Goal = getMetricGoal(CgmMetricEnum.TBR_LEVEL_2);
  const tbrLevel1Goal = getMetricGoal(CgmMetricEnum.TBR_LEVEL_1);
  const tirGoal = getMetricGoal(CgmMetricEnum.TIR);
  const tarLevel1Goal = getMetricGoal(CgmMetricEnum.TAR_LEVEL_1);
  const tarLevel2Goal = getMetricGoal(CgmMetricEnum.TAR_LEVEL_2);

  const tarLevel1Bound = percentGoals?.find(
    i => i.metric === CgmMetricEnum.TAR_LEVEL_1
  )?.thresholdRange?.lowerBound ?? 140;
  const tarLevel2Bound = percentGoals?.find(
    i => i.metric === CgmMetricEnum.TAR_LEVEL_2
  )?.thresholdRange?.lowerBound ?? 200;

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `(${hours}h ${mins}min)`;
    }
    return `(${mins}min)`;
  };

  const renderLegend = (label: string, goal: number, isTooltip?: boolean) => {
    const type = tagTypes.get(label);
    if (isTooltip) {
      return (
        <div className="flex CgmVitalSummaryComponent__legend-tooltip">
          {label === 'TIR' ? `In Range (≥${goal}%)` : `${label} (<${goal}%)`}
        </div>
      );
    }
    return (
      <div className="flex CgmVitalSummaryComponent__legend">
        {type && <TagComponent type={type} iconType="solidDot" background="none" />}
        <div className={label === 'TIR' ? 'CgmVitalSummaryComponent__tir-title' : ''}>
          {label === 'TIR' ? 'In Range' : `${label} (<${goal}%)`}
        </div>
      </div>
    );
  };

  const renderMetric = (value: number, isTir?: boolean) => {
    if (totalMin === undefined || totalMin === 0) {
      return 0;
    }
    const percentage = totalMin !== 0 ? (Math.round((value / totalMin) * 100)) : '0';
    return isDataValid && (
      <div className={`flex ${value === 0 ? 'text-gray-scale-3' : ''}`}>
        <div className={isTir ? 'CgmVitalSummaryComponent__tir-value' : 'b6'}>
          {percentage}
          %
        </div>
        {formatTime(value)}
      </div>
    );
  };

  const cgmBarChartTitle = () => (
    <div className="flex ai-bl">
      <div>
        <strong>Time In Range</strong>
        {' '}
        (&ge;
        {tirGoal || 70}
        %)
      </div>
      {!showGoals && (
        <Tooltip
          placement="top"
          overlayStyle={{ minWidth: '565px' }}
          title={(
            <>
              <div className="mb12">
                <span className="bold">TIR (Time In Range)</span>
                {' '}
                is the percentage of time an individual's glucose levels from a CGM device remain
                within a target range. It provides real-time insight into daily glucose fluctuations
                and complements traditional metrics like A1C by offering a more dynamic view of
                glucose control. Every 5% increase in TIE is clinically beneficial.
              </div>
              <span className="bold">CGM Default Target Ranges</span>
              <div className="flex my5">
                <div className="flex mr32">
                  <div className="mt5 mx5">
                    <Image src={CgmBarChartLabelDm2} preview={false} width={20} height={86} />
                  </div>
                  <div className="mt5 mr12">
                    <Image src={CgmBarChartItem} preview={false} width={60} height={86} />
                  </div>
                  <div>
                    {renderLegend('Very High', 5, true)}
                    {renderLegend('High', 25, true)}
                    {renderLegend('TIR', 70, true)}
                    {renderLegend('Low', 4, true)}
                    {renderLegend('Very Low', 1, true)}
                  </div>
                </div>
                <div className="flex mx32">
                  <div className="mt5 mx5">
                    <Image src={CgmBarChartLabelPreDm} preview={false} width={20} height={86} />
                  </div>
                  <div className="mt5 mr12">
                    <Image src={CgmBarChartItem} preview={false} width={60} height={86} />
                  </div>
                  <div>
                    {renderLegend('Very High', 5, true)}
                    {renderLegend('High', 25, true)}
                    {renderLegend('TIR', 70, true)}
                    {renderLegend('Low', 4, true)}
                    {renderLegend('Very Low', 1, true)}
                  </div>
                </div>
              </div>
              <div className="flex CgmVitalSummaryComponent__tooltip mb12">
                <div>
                  DM1/DM2
                </div>
                <div>
                  Pre-DM
                </div>
              </div>
            </>
          )}
        >
          <InfoCircleOutlined />
        </Tooltip>
      )}
    </div>
  );

  const renderBarChart = () => {
    if (totalMin === undefined || totalMin === 0) {
      return null;
    }

    const tarLevel2Height = tarLevel2Min && tarLevel2Min > 0 ? Math.max((
      tarLevel2Min / totalMin) * barChartHeight, 0.1) : 0;
    const tarLevel1Height = tarLevel1Min && tarLevel1Min > 0 ? Math.max((
      tarLevel1Min / totalMin) * barChartHeight, 0.1) : 0;
    const tirHeight = tirMin && tirMin > 0 ? Math.max((
      tirMin / totalMin) * barChartHeight, 0.1) : 0;
    const tbrLevel1Height = tbrLevel1Min && tbrLevel1Min > 0 ? Math.max((
      tbrLevel1Min / totalMin) * barChartHeight, 0.1) : 0;
    const tbrLevel2Height = tbrLevel2Min && tbrLevel2Min > 0 ? Math.max((
      tbrLevel2Min / totalMin) * barChartHeight, 0.1) : 0;

    return (
      <div className="flex">
        <div className="CgmVitalSummaryComponent__label ml5">
          {/* Y axis labels for CGM bar chart */}
          <YAxis
            tarLevel2Bound={tarLevel2Bound}
            tarLevel1Bound={tarLevel1Bound}
            tarLevel2Height={tarLevel2Height}
            tarLevel1Height={tarLevel1Height}
            tirHeight={tirHeight}
            tbrLevel1Height={tbrLevel1Height}
            tbrLevel2Height={tbrLevel2Height}
            barChartHeight={barChartHeight}
          />
        </div>
        <div className="flex CgmVitalSummaryComponent__bar-chart">
          <div className="CgmVitalSummaryComponent__tar-level-2-bar" style={{ height: `${tarLevel2Height}px` }} />
          <div className="CgmVitalSummaryComponent__tar-level-1-bar" style={{ height: `${tarLevel1Height}px` }} />
          <div className="CgmVitalSummaryComponent__tir-bar" style={{ height: `${tirHeight}px` }} />
          <div className="CgmVitalSummaryComponent__tbr-level-1-bar" style={{ height: `${tbrLevel1Height}px` }} />
          <div className="CgmVitalSummaryComponent__tbr-level-2-bar" style={{ height: `${tbrLevel2Height}px` }} />
        </div>
      </div>
    );
  };

  const renderGoals = () => {
    if (!showGoals) {
      return null;
    }
    return (
      <div className="CgmGoalsComponent bg-gray5 px20 py10 default-border-radius w400">
        <TextComponent className="bold mb5">Targets</TextComponent>
        <div>
          Very High (&lt; 5%)
        </div>
        <div>
          High (&le; 25%)
        </div>
        <div>
          Target (&ge; 70%)
        </div>
        <div>
          Low (&le; 4%)
        </div>
        <div>
          Very Low (&le; 1%)
        </div>
      </div>
    );
  };

  return (
    <Card className="CgmBarChartComponent">
      <div className="flex cmg-bar-chart gap4">
        <div className="flex flex-wrap">
          {cgmBarChartTitle()}
          {!isDataValid ? (<CgmEmptySummaryComponent />) : (
            <div className="flex gap3">
              {renderBarChart()}
              <div className="CgmVitalSummaryComponent__summary-box">
                <div className="flex">
                  {renderLegend('Very High', tarLevel2Goal || 5)}
                  {renderMetric(tarLevel2Min || 0)}
                </div>
                <div className="flex">
                  {renderLegend('High', tarLevel1Goal || 25)}
                  {renderMetric(tarLevel1Min || 0)}
                </div>
                <div className="flex">
                  {renderLegend('TIR', tirGoal || 4)}
                  {renderMetric(tirMin || 0, true)}
                </div>
                <div className="flex">
                  {renderLegend('Low', tbrLevel1Goal || 4)}
                  {renderMetric(tbrLevel1Min || 0)}
                </div>
                <div className="flex">
                  {renderLegend('Very Low', tbrLevel2Goal || 1)}
                  {renderMetric(tbrLevel2Min || 0)}
                </div>
              </div>
            </div>
          )}
        </div>
        {renderGoals()}
      </div>
      <div className="text-gray-scale-2 mt10 fs1">Each 5% increase in time in range is clinically beneficial.</div>
    </Card>
  );
};
